import PropTypes from "prop-types";
import { useState, useEffect } from "react";

// stores
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";

// components
import SectionHeading from "@/components/SectionHeading/SectionHeading";
import TabGroup from "@/components/TabGroup/TabGroup";
import Connectomics from "./Connectomics";

// -----------------------------------------------------------------------------

export default function ConnectomicsTabGroup({ isLinkMode }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;

	// We read the array of experiments from the store
	const experimentsArr = useStore((state) => state.connectomicsExperimentsArr);
	const addConnectomicsExp = useStore(
		(state) => state.addConnectomicsExperiment,
	);
	const removeConnectomicsExp = useStore(
		(state) => state.removeConnectomicsExperiment,
	);

	// -----------------------------------------------------------------------------

	// Initialize activeTab state
	const [activeTabId, setActiveTabId] = useState(experimentsArr[0].id);

	// Generate tabList
	const tabList = experimentsArr.map((exp) => ({
		id: exp.id,
		label: `Experiment ${exp.displayNumber}`,
		shortLabel: `Exp ${exp.displayNumber}`,
		content: <Connectomics expId={exp.id} isLinkMode={isLinkMode} />,
	}));

	// -----------------------------------------------------------------------------

	function handleAddTab() {
		// Clone from the active tab and get the new ID
		const newId = addConnectomicsExp(activeTabId);
		// Switch to the new tab
		setActiveTabId(newId);
	}

	function handleCloseTab(tabIdToClose) {
		// Prevent closing the last tab
		if (experimentsArr.length <= 1) return;

		// Find the index of the tab to close *before* removing it
		const indexToClose = experimentsArr.findIndex(
			(exp) => exp.id === tabIdToClose,
		);

		// Remove the experiment
		removeConnectomicsExp(tabIdToClose);

		// If the closed tab was the active one, determine the new active tab
		if (tabIdToClose === activeTabId) {
			// Get the updated array *after* removal
			const updatedExpArr = useStore.getState().connectomicsExperimentsArr;

			// Determine the index to switch to
			// If closing the first tab (index 0), new active index is 0
			// Otherwise, new active index is the one to the left (index - 1)
			const newActiveIndex = Math.max(0, indexToClose - 1);

			// Set the new active tab ID
			setActiveTabId(updatedExpArr[newActiveIndex].id);
		}
		// If a non-active tab was closed, the activeTabId remains the same.
	}

	// -----------------------------------------------------------------------------

	// Effect to synchronize activeTab with the store's experimentsArr
	// Handles external changes like resetAll
	useEffect(() => {
		// Use the experimentsArr from the component scope, which is in the deps array
		const currentExpArr = experimentsArr;

		// Check if the currently active tab still exists in the array
		const activeTabExists = currentExpArr.some((exp) => exp.id === activeTabId);

		// If the active tab ID is no longer valid and the array is not empty,
		// default to the first available experiment's ID.
		if (!activeTabExists && currentExpArr.length > 0) {
			setActiveTabId(currentExpArr[0].id);
		}
		// If the array is empty, activeTabId remains unchanged (or could be handled differently if needed)
	}, [experimentsArr, activeTabId]); // Correct dependencies: runs when experimentsArr or activeTabId changes

	// -----------------------------------------------------------------------------

	return (
		<section>
			<SectionHeading heading="Connectomics" number={3} />

			<TabGroup
				tabList={tabList}
				activeTab={activeTabId}
				onChangeTab={setActiveTabId}
				onAddTab={handleAddTab}
				onCloseTab={handleCloseTab}
				canCloseTabs={experimentsArr.length > 1}
			/>
		</section>
	);
}

ConnectomicsTabGroup.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
};
